import { StripeProducts } from '@racemap/sdk/schema/billing';
import { SimCardStates } from '@racemap/sdk/schema/simCard';
import { Modal, Typography } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { toast } from 'react-toastify';
import { Placeholder } from 'src/components/BasicComponents/Placeholder';
import { getProductPriceFactory } from 'src/components/CostCalculator/getProductPriceFactory';
import { formatPrice } from 'src/misc/pricing';
import { useStore } from 'src/store/reducers';
import type { ManagedTracker } from 'src/store/trackers/trackers_reducers';
import { type PriceList, useTrackersForAction } from '../../../lib/customHooks';

const { Text, Link } = Typography;

interface Props {
  priceList?: PriceList;
}

export const ActivateSimCardsButton: FC<Props> = ({ priceList }) => {
  const { activateSimCards } = useStore((s) => s.trackers.actions);
  const trackersToUse = useTrackersForAction();
  const activationPrice = getProductPriceFactory(priceList)?.(
    StripeProducts.SIM_CARD_ACTIVATION,
    1,
  );

  if (trackersToUse == null) return <></>;
  if (activationPrice == null) return <Placeholder />;
  const trackerWithAvailableSims = trackersToUse.filter(isAvailableSimCard);
  const handleClick = async () => {
    if (priceList == null || trackerWithAvailableSims.length === 0) return;

    Modal.confirm({
      title: 'Are you sure you want to activate SIM cards?',
      content: (
        <Text>
          This action will activate the SIM cards for{' '}
          <Text strong>{trackerWithAvailableSims.length}</Text> of the selected trackers. For every
          activated SIM card in a month, we will charge you{' '}
          <Text strong>{formatPrice(activationPrice)}</Text>. For more informations about pricing
          visit our{' '}
          <Link href="https://racemap.com/pricing" target="_blank" rel="noopener noreferrer">
            pricing page
          </Link>
          .
        </Text>
      ),
      onOk: async () => {
        const numActivatedSimCards = await activateSimCards(
          trackerWithAvailableSims.map((tracker) => tracker.id),
        );
        toast.success(`Successfully activated ${numActivatedSimCards} SIM cards!`);
      },
    });
  };

  return (
    <Text
      onClick={() => handleClick()}
      onKeyDown={({ key }) => key === 'Enter' && handleClick()}
      disabled={trackerWithAvailableSims.length === 0}
    >
      Activate
    </Text>
  );
};

export function isAvailableSimCard(tracker: Immutable<ManagedTracker>): boolean {
  return tracker.simCard != null && tracker.simCard.status === SimCardStates.AVAILABLE;
}
